import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import { useDeleteAcademicYear } from '../../hooks/academicYears/useDeleteAcademicYear';
import { useDeleteAllAcademicYears } from '../../hooks/academicYears/useDeleteAllAcademicYears';
import { useForceDeleteAcademicYear } from '../../hooks/academicYears/useForceDeleteAcademicYear';
import { useForceDeleteAllAcademicYears } from '../../hooks/academicYears/useForceDeleteAllAcademicYears';
import { useRestoreAcademicYear } from '../../hooks/academicYears/useRestoreAcademicYear';
import { useRestoreAllAcademicYears } from '../../hooks/academicYears/useRestoreAllAcademicYears';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import AcademicYearsTableColumns from './Table/AcademicYearsTableColumns';
import AcademicYearsTableRow from './Table/AcademicYearsTableRow';

const AcademicYearsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const perms = useMemo(() => ({
        list: has('academic_years_list'),
        create: has('academic_years_create'),
        edit: has('academic_years_edit'),
        delete: has('academic_years_delete'),
        show: has('academic_years_show'),
        archive_list: has('academic_years_archive_list'),
        restore: has('academic_years_restore'),
        force_delete: has('academic_years_force_delete'),
    }), [has]);

    const { deleteAcademicYear, isDelete } = useDeleteAcademicYear();
    const { deleteAllAcademicYears, isDelete: isDeletingAll } = useDeleteAllAcademicYears();
    const { forceDeleteAcademicYear } = useForceDeleteAcademicYear();
    const { forceDeleteAllAcademicYears } = useForceDeleteAllAcademicYears();
    const { restoreAcademicYear } = useRestoreAcademicYear();
    const { restoreAllAcademicYears } = useRestoreAllAcademicYears();

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/academic-years',
        deleteSingle: deleteAcademicYear,
        deleteAll: deleteAllAcademicYears,
        forceDeleteSingle: forceDeleteAcademicYear,
        forceDeleteAll: forceDeleteAllAcademicYears,
        restoreSingle: restoreAcademicYear,
        restoreAll: restoreAllAcademicYears,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, isArchive]);

    const { isLoading, error, academicYears, meta } = useGetAcademicYears(page, perPage, filters);

    const renderRow = (params) => (
        <AcademicYearsTableRow
            key={params.key || params.item.id}
            item={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={academicYears.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {!isArchive && perms.archive_list && (
                    <ArchiveButton to="/academic-years/archive" />
                )}
                {isArchive && perms.list && (
                    <ArchiveButton
                        to="/academic-years"
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to List"
                    />
                )}
            </Box>

            <TableLayout
                title={isArabic
                    ? (isArchive ? 'أرشيف السنوات الدراسية' : 'السنوات الدراسية')
                    : (isArchive ? 'Academic Years Archive' : 'Academic Years')}
                subtitle={isArabic
                    ? (isArchive ? 'إدارة السنوات المحذوفة' : 'إدارة السنوات الدراسية في النظام')
                    : (isArchive ? 'Manage deleted academic years' : 'Manage academic years')}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة سنة دراسية' : 'Add Academic Year'}
                onAddClick={() => navigate('/academic-years/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading}
                error={error}
                items={academicYears}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, academicYears)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={AcademicYearsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive
                    ? (perms.force_delete ? handleMenuDelete : undefined)
                    : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد سنوات في الأرشيف' : 'لا توجد سنوات دراسية'}
                emptyLabelEn={isArchive ? 'No archived academic years' : 'No academic years'}
                loadErrorAr="حدث خطأ أثناء تحميل السنوات الدراسية"
                loadErrorEn="Failed to load academic years"
            />
        </>
    );
};

export default AcademicYearsTable;
